import {
  Component,
  DestroyRef,
  Input,
  inject,
  OnChanges,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { DashboardFlightListItem, UAV } from '../../../models/report.model';
import {
  ColumnDefinition,
  DataColumnDefinition,
} from '../../qs-table/column-definition.interface';

type DashboardFlightTableRow = {
  rank: number;
  uav: UAV;
  flightTimeHoursValue: number;
  flightRatioPercent: number;
};

@Component({
  selector: 'app-dashboard-flight-list',
  templateUrl: './dashboard-flight-list.component.html',
  styleUrls: ['./dashboard-flight-list.component.scss'],
  standalone: false,
})
export class DashboardFlightListComponent implements OnInit, OnChanges {
  @Input() title = '';
  @Input() subtitle = '';
  @Input() items: DashboardFlightListItem[] = [];
  @Input() accent = '#0f766e';
  @Input() totalFlightSeconds = 0;

  @ViewChild('flightVisualTemplate', { static: true })
  private flightVisualTemplate!: TemplateRef<DashboardFlightTableRow>;

  isCollapsed = false;
  tableRows: DashboardFlightTableRow[] = [];
  columns: ColumnDefinition<DashboardFlightTableRow>[] = [];
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly router: Router,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.columns = this.buildColumns();
    this.rebuildRows();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.columns = this.buildColumns();
      });
  }

  ngOnChanges(): void {
    this.rebuildRows();
  }

  toggleCollapsed(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  private buildColumns(): ColumnDefinition<DashboardFlightTableRow>[] {
    return [
      {
        type: 'data',
        field: 'rank',
        header: '#',
        width: '70px',
        sortFn: (
          a: DashboardFlightTableRow,
          b: DashboardFlightTableRow,
          multiplier: number,
        ) => (a.rank - b.rank) * multiplier,
      } as DataColumnDefinition<DashboardFlightTableRow>,
      {
        type: 'data',
        field: 'uav',
        header: this.translate.instant('dashboard.table.uav'),
        width: '340px',
        sortFn: (
          a: DashboardFlightTableRow,
          b: DashboardFlightTableRow,
          multiplier: number,
        ) =>
          this.formatUavLabel(a.uav).localeCompare(this.formatUavLabel(b.uav)) *
          multiplier,
        format: (row?: DashboardFlightTableRow): string =>
          this.formatUavLabel(row?.uav ?? {}),
        tooltip: (row: DashboardFlightTableRow): string =>
          this.formatUavTooltip(row.uav),
        onClick: (row: DashboardFlightTableRow): void => {
          this.navigateToDroneDetails(row.uav);
        },
        style: (row: DashboardFlightTableRow) => ({
          'font-weight': '600',
          cursor: this.hasDeviceId(row.uav) ? 'pointer' : 'default',
        }),
      } as DataColumnDefinition<DashboardFlightTableRow>,
      {
        type: 'data',
        field: 'flightTimeHoursValue',
        header: this.translate.instant('dashboard.table.flight_time_hours'),
        sortFn: (
          a: DashboardFlightTableRow,
          b: DashboardFlightTableRow,
          multiplier: number,
        ) => (a.flightTimeHoursValue - b.flightTimeHoursValue) * multiplier,
        format: (row?: DashboardFlightTableRow): string =>
          (row?.flightTimeHoursValue ?? 0).toFixed(2),
      } as DataColumnDefinition<DashboardFlightTableRow>,
      {
        type: 'data',
        field: 'flightRatioPercent',
        header: this.translate.instant(
          'dashboard.table.total_flight_hours_pct',
        ),
        width: '220px',
        customTemplate: this.flightVisualTemplate,
        sortFn: (
          a: DashboardFlightTableRow,
          b: DashboardFlightTableRow,
          multiplier: number,
        ) => (a.flightRatioPercent - b.flightRatioPercent) * multiplier,
      } as DataColumnDefinition<DashboardFlightTableRow>,
    ];
  }

  private rebuildRows(): void {
    const topItems = this.items.slice(0, 10);
    const fleetTotalSeconds =
      this.totalFlightSeconds > 0 ? this.totalFlightSeconds : 0;

    this.tableRows = topItems.map((item, index) => ({
      rank: index + 1,
      uav: item.uav,
      flightTimeHoursValue: item.flightTime / 3600,
      flightRatioPercent:
        fleetTotalSeconds > 0
          ? Math.min(
              100,
              Math.round((item.flightTime / fleetTotalSeconds) * 100),
            )
          : 0,
    }));
  }

  // The cell already shows unit and model, so the tooltip adds the operator-assigned name when
  // there is one. Falling back to an empty string leaves the table's default tooltip in place.
  private formatUavTooltip(uav: UAV): string {
    return uav.nameAlias ?? '';
  }

  private formatUavLabel(uav: UAV): string {
    const hardwareRevision = this.toFiniteNumber(uav['hardwareRevision']);
    const unitId = this.toFiniteNumber(uav['unitId']);
    // Reports are always queried with resolveDevices=true, so the model name comes from the device
    // the backend resolved. There is deliberately no local hardware-revision lookup table here: it
    // would be a second, drifting copy of the backend model registry.
    const modelName = uav.modelName;

    if (unitId !== null && modelName) {
      return `${unitId} (${modelName})`;
    }

    if (unitId !== null && hardwareRevision !== null) {
      return `${unitId} (HR ${hardwareRevision})`;
    }

    if (unitId !== null) {
      return `${unitId}`;
    }

    if (modelName) {
      return modelName;
    }

    if (hardwareRevision !== null) {
      return `HR ${hardwareRevision}`;
    }

    return Object.keys(uav).length > 0
      ? JSON.stringify(uav)
      : this.translate.instant('dashboard.labels.unknown_uav');
  }

  private toFiniteNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }

  private navigateToDroneDetails(uav: UAV): void {
    if (!this.hasDeviceId(uav)) {
      return;
    }

    this.router.navigateByUrl(`/device/${uav.deviceId}`);
  }

  private hasDeviceId(uav: UAV): boolean {
    return typeof uav.deviceId === 'string' && uav.deviceId.trim().length > 0;
  }
}
